import { useState, useCallback, useRef } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiArrowUpTray, HiCheck, HiXMark, HiFolderOpen } from 'react-icons/hi2';
import {
  UploadZone,
  UploadZoneIcon,
  UploadZoneTitle,
  UploadZoneSubtitle,
  UploadZoneFormats,
  BrowseFolderButton,
  UploadProgressCard,
  UploadProgressFile,
  UploadProgressFilename,
  UploadProgressSize,
  UploadProgressBar,
  UploadProgressBarFill,
  HiddenFileInput,
} from './DocumentUpload.styled';

const ACCEPTED_TYPES: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/rtf': ['.rtf'],
  'text/rtf': ['.rtf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.rtf', '.jpg', '.jpeg', '.png'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

function hasAllowedExtension(fileName: string): boolean {
  const ext = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
  return ALLOWED_EXTENSIONS.includes(ext);
}

export interface UploadFileEntry {
  file: File;
  documentName?: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

export interface FileUploadZoneProps {
  /** Called for each accepted file. Return a promise that resolves when upload is done. */
  onUploadFile: (file: File) => Promise<void>;
  /** Whether upload is currently disabled (e.g. limit reached, missing captcha) */
  disabled?: boolean;
  /** Optional: show the naming modal before upload (used on the authenticated page) */
  onFilesAccepted?: (files: File[]) => void;
  /** If true, files are passed to onFilesAccepted instead of uploaded directly */
  useNamingModal?: boolean;
  /** Optional extra buttons rendered after the folder button (e.g. cloud import buttons) */
  extraButtons?: React.ReactNode;
  /** i18n key prefix for labels (default: 'upload') */
  i18nPrefix?: string;
}

export default function FileUploadZone({
  onUploadFile,
  disabled = false,
  onFilesAccepted,
  useNamingModal = false,
  extraButtons,
  i18nPrefix = 'upload',
}: FileUploadZoneProps) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<UploadFileEntry[]>([]);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const uploadFiles = useCallback(
    async (acceptedFiles: File[]) => {
      const newEntries: UploadFileEntry[] = acceptedFiles.map((file) => ({
        file,
        status: 'pending' as const,
      }));

      setFiles((prev) => [...prev, ...newEntries]);

      for (const entry of newEntries) {
        setFiles((prev) =>
          prev.map((f) => (f.file === entry.file ? { ...f, status: 'uploading' } : f)),
        );

        try {
          await onUploadFile(entry.file);
          setFiles((prev) =>
            prev.map((f) => (f.file === entry.file ? { ...f, status: 'done' } : f)),
          );
        } catch {
          setFiles((prev) =>
            prev.map((f) =>
              f.file === entry.file
                ? { ...f, status: 'error', error: t(`${i18nPrefix}.uploadFailed`) }
                : f,
            ),
          );
        }
      }
    },
    [onUploadFile, t, i18nPrefix],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[], rejections: FileRejection[]) => {
      const rescued = rejections
        .filter((r) => r.errors.every((e) => e.code === 'file-invalid-type'))
        .map((r) => r.file)
        .filter((f) => hasAllowedExtension(f.name) && f.size <= MAX_FILE_SIZE);

      const allAccepted = [...acceptedFiles, ...rescued];
      if (allAccepted.length === 0) return;

      if (useNamingModal && onFilesAccepted) {
        onFilesAccepted(allAccepted);
      } else {
        uploadFiles(allAccepted);
      }
    },
    [uploadFiles, useNamingModal, onFilesAccepted],
  );

  const handleFolderSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList) return;

      const filtered = Array.from(fileList).filter(
        (f) => hasAllowedExtension(f.name) && f.size <= MAX_FILE_SIZE,
      );

      if (filtered.length > 0) {
        if (useNamingModal && onFilesAccepted) {
          onFilesAccepted(filtered);
        } else {
          uploadFiles(filtered);
        }
      }

      e.target.value = '';
    },
    [uploadFiles, useNamingModal, onFilesAccepted],
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    disabled,
  });

  const clearCompleted = () => {
    setFiles((prev) => prev.filter((f) => f.status !== 'done'));
  };

  return (
    <div>
      <UploadZone
        {...getRootProps()}
        $active={isDragActive}
        $rejected={fileRejections.length > 0}
        style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
      >
        <input {...getInputProps()} disabled={disabled} />
        <UploadZoneIcon>
          <HiArrowUpTray />
        </UploadZoneIcon>
        <UploadZoneTitle>
          {isDragActive ? t(`${i18nPrefix}.dropHere`) : t(`${i18nPrefix}.dragDrop`)}
        </UploadZoneTitle>
        <UploadZoneSubtitle>{t(`${i18nPrefix}.browse`)}</UploadZoneSubtitle>
        <UploadZoneFormats>{t(`${i18nPrefix}.formats`)}</UploadZoneFormats>
      </UploadZone>

      <HiddenFileInput
        ref={folderInputRef}
        type="file"
        // @ts-expect-error webkitdirectory is not in React's input type definitions
        webkitdirectory=""
        directory=""
        multiple
        onChange={handleFolderSelect}
      />
      <BrowseFolderButton
        onClick={() => folderInputRef.current?.click()}
        disabled={disabled}
      >
        <HiFolderOpen />
        {t(`${i18nPrefix}.browseFolder`)}
      </BrowseFolderButton>

      {extraButtons}

      {fileRejections.length > 0 && (
        <Alert variant="danger" className="mt-3">
          {fileRejections.map(({ file, errors }) => (
            <div key={file.name}>
              <strong>{file.name}</strong>: {errors.map((e) => e.message).join(', ')}
            </div>
          ))}
        </Alert>
      )}

      {files.length > 0 && (
        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">{t(`${i18nPrefix}.queue`)}</h6>
            {files.some((f) => f.status === 'done') && (
              <button className="btn btn-sm btn-link" onClick={clearCompleted}>
                {t(`${i18nPrefix}.clearCompleted`)}
              </button>
            )}
          </div>
          {files.map((f, idx) => (
            <UploadProgressCard key={`${f.file.name}-${idx}`}>
              <UploadProgressFile>
                <div className="d-flex align-items-center gap-2">
                  {f.status === 'uploading' && <Spinner animation="border" size="sm" />}
                  {f.status === 'done' && <HiCheck color="green" />}
                  {f.status === 'error' && <HiXMark color="red" />}
                  <UploadProgressFilename>{f.documentName ?? f.file.name}</UploadProgressFilename>
                </div>
                <UploadProgressSize>
                  {(f.file.size / (1024 * 1024)).toFixed(1)} MB
                </UploadProgressSize>
              </UploadProgressFile>
              <UploadProgressBar>
                <UploadProgressBarFill
                  $status={f.status}
                  style={{
                    width:
                      f.status === 'pending'
                        ? '0%'
                        : f.status === 'uploading'
                          ? '60%'
                          : '100%',
                  }}
                />
              </UploadProgressBar>
              {f.error && <small className="text-danger">{f.error}</small>}
            </UploadProgressCard>
          ))}
        </div>
      )}
    </div>
  );
}
